<template>
  <div>
    <DropdownMenu v-if="visibleActions.length > 0">
      <DropdownMenuTrigger as-child>
        <button
          class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-text-secondary-500 hover:tw-text-white tw-bg-white tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
          type="button"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-light">
              {{ t('common.actions.bulk_actions') }}
            </span>
            <DotsVerticalIcon class="tw-w-4 tw-h-4" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="tw-w-56 !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]">
        <DropdownMenuItem
          v-for="action in visibleActions"
          :key="action.label"
          @click="action.onClick"
          class="tw-cursor-pointer hover:!tw-bg-gray-100 tw-transition-colors tw-group"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <component
              :is="action.icon"
              class="tw-w-4 tw-h-4"
              :class="action.iconClass"
              v-if="action.icon"
            />
            <span>{{ action.label }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('common.forms.confirmation').toUpperCase()"
      :message="confirmationMessage"
      :type="confirmationModalType"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onConfirmAction"
      @cancel="closeConfirmationModal"
    />

    <!-- Request clarification modal -->
    <GenericModal
      v-model="showRequestClarificationModal"
      :title="t('payments.titles.confirm_clarification')"
    >
      <template #body>
        <div class="tw-mb-4">
          <Textarea
            v-model="clarificationComment"
            class="tw-border tw-w-full tw-px-3 tw-py-2 tw-rounded tw-resize-none"
            :placeholder="t('payments.titles.message_hint')"
            rows="6"
          ></Textarea>
        </div>
      </template>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2 tw-items-center">
          <Button
            class="tw-bg-white tw-border-solid tw-border-2 tw-border-secondary-500 tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            type="button"
            @click="onCloseRequestClarificationModal"
          >
            {{ t('common.forms.cancel') }}
          </Button>
          <Button
            class="tw-bg-secondary-500 tw-border-2 tw-border-secondary-500 tw-text-white hover:tw-bg-secondary-600 tw-border-solid tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            :class="{
              'tw-opacity-50 !tw-cursor-not-allowed': !clarificationComment,
            }"
            type="button"
            @click="performClarificationAction"
            :disabled="!clarificationComment"
          >
            {{ t('requests.actions.request_clarification') }}
          </Button>
        </div>
      </template>
    </GenericModal>

    <!-- Export modal -->
    <GenericModal
      v-model="showExportModal"
      :title="t('common.actions.export')"
      extra-class="tw-min-w-[800px]"
    >
      <template #body>
        <ExportForm
          :fields="exportFields"
          :allow-pdf="isPdfExportEnabled"
          @export="exportAllPayments"
          @cancel="closeExportModal"
        ></ExportForm>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import ApproveIcon from '@/assets/icons/Approve.svg';
import CloseIcon from '@/assets/icons/Close.svg';
import DotsVerticalIcon from '@/assets/icons/DotsVertical.svg';
import DeleteIcon from '@/assets/icons/Trash.svg';
import FileExportIcon from '@/assets/icons/FileExport.svg';
import ForwardIcon from '@/assets/icons/Forward.svg';
import QuestionIcon from '@/assets/icons/Question.svg';
import CheckTextIcon from '@/assets/icons/CheckText.svg';
import PaidIcon from '@/assets/icons/PaidIcon.svg';

import { ConfirmationModal, GenericModal } from '@/components/shared';
import ExportForm from '@/components/shared/export/export-form.vue';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePaymentStore } from '@/stores/payment.store';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { currentUser } = storeToRefs(useUserStore());
const { paymentsBulkAction, createPaymentComments, exportPayments } = usePaymentStore();

// Action type enum
enum ActionType {
  DELETE = 'delete',
  APPROVE = 'approve',
  REJECT = 'reject',
  CLARIFICATION = 'clarification',
  SUBMIT = 'submit',
  MARK_CLARIFICATIONS_COMPLETED = 'mark_clarifications_completed',
  EXPORT = 'export',
  MARK_AS_PAID = 'mark_as_paid',
}

const props = defineProps<{
  selectedPayments: any[];
}>();

const emit = defineEmits<{
  reloadTable: [];
  clearSelection: [];
}>();

const showConfirmationModal = ref(false);
const showExportModal = ref(false);
const confirmationMessage = ref('');
const currentActionType = ref<ActionType | null>(null);
const confirmationModalType = ref<'warning' | 'danger' | 'info' | 'success'>('warning');

// Request clarification modal
const showRequestClarificationModal = ref(false);
const clarificationComment = ref('');

const showBulkDeleteBtn = () => {
  return (
    props.selectedPayments.length > 0 &&
    props.selectedPayments.filter((item: any) => {
      return item.status === 'DRAFT';
    }).length == props.selectedPayments.length
  );
};

const showBulkRequestClarificationBtn = () => {
  return (
    props.selectedPayments.length &&
    props.selectedPayments.filter((item: any) => {
      return (
        ['PENDING_APPROVAL', 'CLEARING'].includes(item.status_display) &&
        !item.is_clarification_requested &&
        (currentUser.value?.user_role?.includes('Payment Clearer') ||
          currentUser.value?.user_role?.includes('Approver') ||
          currentUser.value?.user_role?.includes('Admin'))
      );
    }).length == props.selectedPayments.length
  );
};

const showBulkMarkClarificationsCompletedBtn = () => {
  return (
    props.selectedPayments.filter((item: any) => {
      return (
        item.is_clarification_requested &&
        (currentUser.value?.id === item.created_by ||
          currentUser.value?.id === item.requested_clarification_by)
      );
    }).length === props.selectedPayments.length
  );
};

const showBulkApproveBtn = () => {
  if (currentUser.value?.user_role?.includes('Admin')) {
    return (
      props.selectedPayments.length &&
      props.selectedPayments.filter((item: any) => {
        return item.status_display === 'CLEARING';
      }).length === props.selectedPayments.length
    );
  }
  if (currentUser.value?.user_role?.includes('Approver')) {
    return (
      props.selectedPayments.length &&
      props.selectedPayments.filter((item: any) => {
        return item.status_display === 'PENDING_APPROVAL';
      }).length == props.selectedPayments.length
    );
  }
  if (currentUser.value?.user_role?.includes('Payment Clearer')) {
    return (
      props.selectedPayments.length &&
      props.selectedPayments.filter((item: any) => {
        return item.status_display === 'PENDING_APPROVAL';
      }).length == props.selectedPayments.length
    );
  }
  return false;
};

const showBulkRejectBtn = () => {
  return (
    (currentUser.value?.user_role?.includes('Admin') ||
      currentUser.value?.user_role?.includes('Approver') ||
      currentUser.value?.user_role?.includes('Payment Clearer')) &&
    props.selectedPayments.length &&
    props.selectedPayments.filter((item: any) => {
      return item.status_display == 'CLEARING' || item.status_display == 'PENDING_APPROVAL';
    }).length === props.selectedPayments.length
  );
};

const performApproveAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_approve', paymentIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('payments.titles.successful_bulk_approval'));
};

// const performSubmitAction = async () => {
//   const paymentIds = props.selectedPayments.map(payment => payment.id);
//   await handleApiError(async () => {
//     await paymentsBulkAction('bulk_submit', paymentIds);
//     emit('clearSelection');
//     emit('reloadTable');
//   }, t('payments.titles.successful_bulk_submit'));
// };

const performRejectAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_reject', paymentIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('payments.titles.successful_bulk_rejection'));
};

const performDeleteAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_delete', paymentIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('payments.titles.successful_bulk_delete'));
};

const performClarificationAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_request_clarification', paymentIds);

    if (clarificationComment.value !== '') {
      const obj = {
        payment: null,
        text: clarificationComment.value,
      };

      for (const id of paymentIds) {
        obj.payment = id;
        await createPaymentComments(obj);
      }
    }

    emit('clearSelection');
    emit('reloadTable');
    onCloseRequestClarificationModal();
  }, t('payments.titles.successful_bulk_clarification'));
};

const performMarkClarificationsCompletedAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_respond_to_clarification_request', paymentIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('payments.titles.successful_mark_clarifications_completed'));
};

const performMarkAsPaidAction = async () => {
  const paymentIds = props.selectedPayments.map(payment => payment.id);
  await handleApiError(async () => {
    await paymentsBulkAction('bulk_mark_as_paid', paymentIds);
    emit('clearSelection');
    emit('reloadTable');
  }, t('payments.titles.successful_mark_as_paid'));
};

const onCloseRequestClarificationModal = () => {
  showRequestClarificationModal.value = false;
  clarificationComment.value = '';
};

const openRequestClarificationModal = () => {
  currentActionType.value = ActionType.CLARIFICATION;
  showRequestClarificationModal.value = true;
};

const openExportModal = () => {
  showExportModal.value = true;
};

const closeExportModal = () => {
  showExportModal.value = false;
};

const actionMap = {
  [ActionType.APPROVE]: performApproveAction,
  [ActionType.REJECT]: performRejectAction,
  [ActionType.DELETE]: performDeleteAction,
  [ActionType.CLARIFICATION]: performClarificationAction,
  // [ActionType.SUBMIT]: performSubmitAction,
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: performMarkClarificationsCompletedAction,
  [ActionType.MARK_AS_PAID]: performMarkAsPaidAction,
};

const actionTypeToMessageMap = {
  [ActionType.APPROVE]: t('payments.titles.confirm_bulk_approval'),
  [ActionType.REJECT]: t('payments.titles.confirm_bulk_rejection'),
  [ActionType.DELETE]: t('payments.titles.confirm_bulk_delete'),
  // [ActionType.SUBMIT]: t('payments.titles.confirm_submit'),
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: t(
    'payments.titles.confirm_mark_clarifications_completed'
  ),
  [ActionType.MARK_AS_PAID]: t('payments.titles.confirm_mark_as_paid'),
};

const actionTypeToModalTypeMap = {
  [ActionType.APPROVE]: 'success',
  [ActionType.REJECT]: 'warning',
  [ActionType.DELETE]: 'danger',
} as const;

const openConfirmationModal = (actionType: ActionType) => {
  confirmationModalType.value = actionTypeToModalTypeMap[actionType];
  currentActionType.value = actionType;
  confirmationMessage.value = actionTypeToMessageMap[actionType];
  showConfirmationModal.value = true;
};

const onConfirmAction = async () => {
  if (currentActionType.value === null) return;
  await actionMap[currentActionType.value]();
};

const closeConfirmationModal = () => {
  showConfirmationModal.value = false;
};

const actions = shallowRef<any[]>([
  // Submit action disabled — submission must go through the details page
  // to ensure the AdjustPaymentForSubmission modal is shown
  // {
  //   label: t('common.forms.submit'),
  //   icon: ForwardIcon,
  //   iconClass: 'tw-text-blue-500',
  //   onClick: () => {
  //     openConfirmationModal(ActionType.SUBMIT);
  //   },
  //   show: () => {
  //     return showBulkSubmitDeleteBtn();
  //   },
  // },
  {
    label: t('requests.actions.approve'),
    icon: ApproveIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.APPROVE);
    },
    show: () => {
      return showBulkApproveBtn();
    },
  },
  {
    label: t('requests.actions.request_clarification'),
    icon: QuestionIcon,
    iconClass: 'tw-text-yellow-500',
    onClick: () => {
      openRequestClarificationModal();
    },
    show: () => {
      return showBulkRequestClarificationBtn();
    },
  },
  {
    label: t('orders.actions.mark_clarifications_completed'),
    icon: CheckTextIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_CLARIFICATIONS_COMPLETED);
    },
    show: () => {
      return showBulkMarkClarificationsCompletedBtn();
    },
  },
  {
    label: t('requests.actions.reject'),
    icon: CloseIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.REJECT);
    },
    show: () => {
      return showBulkRejectBtn();
    },
  },
  {
    label: t('common.forms.delete'),
    icon: DeleteIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.DELETE);
    },
    show: () => {
      return showBulkDeleteBtn();
    },
  },
  {
    label: t('common.forms.mark_as_paid'),
    icon: PaidIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_AS_PAID);
    },
    show: () => {
      return showBulkMarkAsPaidBtn();
    },
  },
  {
    label: t('common.actions.export'),
    icon: FileExportIcon,
    iconClass: 'tw-text-blue-500',
    onClick: () => {
      openExportModal();
    },
    show: () => {
      return showBulkExport();
    },
  },
]);

const exportFields = [
  { label: t('payment.titles.payment_no'), value: 'payment_number' },
  { label: t('orders.titles.supplier'), value: 'supplier_name' },
  { label: t('orders.titles.supplier_no'), value: 'supplier_number' },
  { label: t('payment.titles.invoice_no'), value: 'invoice_number' },
  {
    label: t('payment.titles.internal_order_no'),
    value: 'internal_order_number',
  },
  { label: t('payment.titles.due_date'), value: 'due_date' },
  { label: t('payment.titles.payment_amount'), value: 'amount' },
  { label: t('orders.titles.created_on'), value: 'created_on' },
  { label: t('orders.titles.created_by'), value: 'created_by' },
  { label: t('orders.titles.released_by'), value: 'released_by' },
  { label: t('payment.titles.paid'), value: 'payed' },
];

const isPdfExportEnabled = computed(() => {
  return (
    props.selectedPayments.length > 0 &&
    props.selectedPayments.every((payment: any) => payment.status === 'APPROVED')
  );
});

const showBulkExport = () => {
  return (
    props.selectedPayments.length > 0 &&
    props.selectedPayments.filter((payment: any) => {
      return payment.status === 'DRAFT';
    }).length === 0
  );
};

const showBulkMarkAsPaidBtn = () => {
  if (
    currentUser.value?.user_role?.includes('Payment Clearer') ||
    currentUser.value?.user_role?.includes('Payer')
  ) {
    return (
      props.selectedPayments.length > 0 &&
      props.selectedPayments.filter((payment: any) => {
        return payment.status === 'APPROVED';
      }).length === props.selectedPayments.length
    );
  }
  return false;
};

const exportAllPayments = async (data: any) => {
  const selectedPaymentsIds = props.selectedPayments.map((payment: any) => payment.id);
  if (selectedPaymentsIds.length === 0) {
    return;
  }
  const fileFormat = data.exportFormat;
  const fields = data.fields;

  await handleApiError(async () => {
    const exportData = await exportPayments(selectedPaymentsIds, fileFormat, fields);
    closeExportModal();
    window.open(exportData.file_url, '_blank');
  }, t('payments.titles.successful_export'));
};

const visibleActions = computed(() => {
  return actions.value.filter(action => action.show());
});
</script>
